import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import databaseAPI from '../../api/databaseAPI.js';
import shopifyAPI from '../../api/shopifyAPI.js';
import emailService from '../../api/emailService.js';
import '../../styles/OrderDashboard.css';
import useProductImages from '../../hooks/useProductImages';
import * as statusMaps from './statusMaps';
import { formatPaymentStatus, paymentStatusClass } from './paymentUtils';
import {
  downloadStatusSampleCsv,
  parseStatusCsvText,
  processStatusCsvData,
} from './statusCsv';
import {
  downloadOrderCreateSampleCsv as downloadOrderCreateSampleCsvFile,
  parseOrderCreateCsvText,
  processOrderCreateCsv as processOrderCreateCsvRows,
} from './orderCreateCsv';
import { downloadOrdersExportFromServer } from './orderExport';
import AddOrderModal from './AddOrderModal';
import { submitDisputeAction, resolveDisputeAction } from './disputeActions';
import { formatAddress, formatDate, formatISTDateTime, getStatusColor } from './formatUtils';
import DashboardOverlays from './DashboardOverlays';
import EmailHistoryModal from './EmailHistoryModal';
import DisputeManagementPanel from './DisputeManagementPanel';
import DisputeReportModal from './DisputeReportModal';
import OrderDashboardHeader from './OrderDashboardHeader';
import StatusCsvUploadSection from './StatusCsvUploadSection';
import OrderFiltersBar from './OrderFiltersBar';
import OrderDashboardStats from './OrderDashboardStats';

const OrderDashboard = () => {
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [dateFilter, setDateFilter] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [ordersPerPage] = useState(50);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(null);
  const [totalMatching, setTotalMatching] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [backgroundSyncing, setBackgroundSyncing] = useState(false);
  const [isFilterRefreshing, setIsFilterRefreshing] = useState(false);
  const hasCompletedInitialFetch = useRef(false);
  
  // Bulk action states
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);
  
  // Type filter state (preorder vs regular)
  const [selectedType, setSelectedType] = useState('all');
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState('all');
  const [orderScope, setOrderScope] = useState('shipping'); // all | shipping | digital
  
  // CSV Upload states
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [showCsvUpload, setShowCsvUpload] = useState(false);
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [csvResults, setCsvResults] = useState(null);
  
  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportScope, setExportScope] = useState('shipping'); // all | shipping | digital
  const [exporting, setExporting] = useState(false);
  const [showSubscriptionSheet, setShowSubscriptionSheet] = useState(false);
  const [statusPreset, setStatusPreset] = useState('none');
  // Add Order modal state
  const [showAddOrderModal, setShowAddOrderModal] = useState(false);
  const [addOrderMode, setAddOrderMode] = useState('manual'); // 'manual' | 'csv'
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [newOrder, setNewOrder] = useState({
    customer_email: '',
    customer_first_name: '',
    customer_last_name: '',
    total_price: '',
    currency: 'USD',
    coupon_code: '',
    line_items: [],
    shipping_address: { address1: '', city: '', province: '', country: 'India', zip: '', phone_number: '' },
    billing_address: { address1: '', city: '', province: '', country: 'India', zip: '', phone_number: '' }
  });

  // CSV (Create Orders) state
  const [orderCsvFile, setOrderCsvFile] = useState(null);
  const [orderCsvData, setOrderCsvData] = useState([]);
  const [orderCsvProcessing, setOrderCsvProcessing] = useState(false);
  
  // Issue Reported states
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeOrderId, setDisputeOrderId] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeComment, setDisputeComment] = useState('');
  const [disputePhotos, setDisputePhotos] = useState([]);
  const [processingDispute, setProcessingDispute] = useState(false);

  // Custom notification state
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success' // 'success', 'error', 'warning', 'info'
  });
  const [popupState, setPopupState] = useState({
    open: false,
    mode: 'alert', // alert | confirm | prompt
    title: '',
    message: '',
    type: 'info',
    confirmText: 'OK',
    cancelText: 'Cancel',
    inputValue: '',
    resolve: null
  });

  // Issue Reported details modal state
  const [showDisputeDetailsModal, setShowDisputeDetailsModal] = useState(false);
  const [selectedDisputeData, setSelectedDisputeData] = useState(null);

  // Issue Reported management view state
  const [showDisputeView, setShowDisputeView] = useState(false);
  const [emailHistoryOrder, setEmailHistoryOrder] = useState(null);
  const [resendingEmailKey, setResendingEmailKey] = useState('');
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  // Product images (batched from local DB) + modal
  const productImagesCache = useProductImages(orders);
  const [imageModal, setImageModal] = useState({ show: false, imageUrl: '', productTitle: '' });

  // Notification utility functions
  const showNotification = (message, type = 'success', duration = 4000) => {
    setNotification({
      show: true,
      message,
      type
    });
    
    // Auto hide after duration
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, duration);
  };

  const hideNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  const closePopup = (result = null) => {
    const resolver = popupState.resolve;
    setPopupState(prev => ({ ...prev, open: false, resolve: null }));
    if (typeof resolver === 'function') resolver(result);
  };

  const showAlert = (message, title = 'Notice', type = 'info') => {
    setPopupState({
      open: true,
      mode: 'alert',
      title,
      message: String(message || ''),
      type,
      confirmText: 'OK',
      cancelText: 'Cancel',
      inputValue: '',
      resolve: null
    });
  };

  const showConfirm = ({ message, title = 'Please confirm', type = 'warning', confirmText = 'Yes', cancelText = 'Cancel' }) =>
    new Promise((resolve) => {
      setPopupState({
        open: true,
        mode: 'confirm',
        title,
        message: String(message || ''),
        type,
        confirmText,
        cancelText,
        inputValue: '',
        resolve
      });
    });

  const showPrompt = ({ message, title = 'Input Required', type = 'info', confirmText = 'Submit', cancelText = 'Cancel', defaultValue = '' }) =>
    new Promise((resolve) => {
      setPopupState({
        open: true,
        mode: 'prompt',
        title,
        message: String(message || ''),
        type,
        confirmText,
        cancelText,
        inputValue: defaultValue,
        resolve
      });
    });

  // Keep existing calls working while rendering custom center-screen popup.
    const regularStatusMapping = statusMaps.regularStatusMapping;
  const preorderStatusMapping = statusMaps.preorderStatusMapping;
  const regularStatusProgression = statusMaps.regularStatusProgression;
  const preorderStatusProgression = statusMaps.preorderStatusProgression;
  const getStatusMapping = statusMaps.getStatusMapping;
  const getStatusProgression = statusMaps.getStatusProgression;
  const customStatusMap = statusMaps.customStatusMap;
  const mapCsvStatusToSystem = statusMaps.mapCsvStatusToSystem;
  const getStatusIndex = statusMaps.getStatusIndex;
  const isReplacementStatus = statusMaps.isReplacementStatus;
  const isStatusChangeAllowed = statusMaps.isStatusChangeAllowed;

// Check if email was sent for this status (from database)
  const isEmailSent = (orderId, status) => {
    const order = orders.find(o => o.id === orderId);
    if (!order || !order.email_status) return false;
    return order.email_status[status]?.sent === true;
  };

  // Check if email failed for this status (from database)
  const isEmailFailed = (orderId, status) => {
    const order = orders.find(o => o.id === orderId);
    if (!order || !order.email_status) return false;
    return order.email_status[status]?.failed === true;
  };

  // Get email error message (from database)
  const getEmailError = (orderId, status) => {
    const order = orders.find(o => o.id === orderId);
    if (!order || !order.email_status) return 'Unknown error';
    return order.email_status[status]?.error || 'Unknown error';
  };

  const mergeOrderDetail = (detail) => {
    if (!detail?.id) return;
    setOrders((prev) => prev.map((o) => (o.id === detail.id ? { ...o, ...detail } : o)));
  };

  const openEmailHistory = async (order) => {
    setEmailHistoryOrder({ ...order, email_logs: [], _loading: true });
    try {
      const res = await databaseAPI.getOrderDashboardDetail(order.id);
      if (res.success && res.order) {
        mergeOrderDetail(res.order);
        setEmailHistoryOrder(res.order);
        return;
      }
    } catch (_e) {
      /* use list row */
    }
    setEmailHistoryOrder(order);
  };

  const getEmailTooltipText = (order) => {
    const logs = Array.isArray(order?.email_logs) ? order.email_logs : [];
    const latestFromList = order?.latest_email;
    if (!logs.length && latestFromList) {
      return [
        `Latest: ${(latestFromList.sent_status || 'pending').toUpperCase()}`,
        `Status: ${latestFromList.status || '-'}`,
        `Subject: ${latestFromList.subject || '-'}`,
        `Time (IST): ${formatISTDateTime(latestFromList.created_at)}`,
        'Click to view all emails',
      ].join('\n');
    }
    if (!logs.length) {
      if (isEmailSent(order?.id, order?.custom_status)) return 'Email sent\nClick to view details';
      if (isEmailFailed(order?.id, order?.custom_status)) return `Email failed\n${getEmailError(order?.id, order?.custom_status)}\nClick to view details`;
      return 'No email logs found';
    }
    const latest = logs[0];
    return [
      `Latest: ${(latest.sent_status || 'pending').toUpperCase()}`,
      `Status: ${latest.status || '-'}`,
      `Subject: ${latest.subject || '-'}`,
      `Time (IST): ${formatISTDateTime(latest.created_at)}`,
      'Click to view all emails',
    ].join('\n');
  };

  const buildOrderEmailDetails = (order) => ({
    id: order.id,
    name: order.customer?.first_name || order.customer_name || '',
    email: order.email,
    order_name: order.name,
    line_items: order.line_items || [],
    shipping_address: order.shipping_address || {},
    billing_address: order.billing_address || {},
  });

  const getOrderInvoiceHref = (order) => (
    order.invoice_url || databaseAPI.getInvoiceDownloadUrl(order.id)
  );

  const handleViewInvoice = (order) => {
    const href = getOrderInvoiceHref(order);
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadInvoice = async (order) => {
    setDownloadingInvoiceId(order.id);
    try {
      const res = await databaseAPI.downloadInvoice(order.id, order.invoice_url || null);
      if (!res.success) {
        throw new Error(res.error || 'Invoice download failed');
      }
      const cleanName = String(order.name || order.id).replace('#', '');
      const blobUrl = URL.createObjectURL(res.blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `invoice-${cleanName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      showNotification(`Invoice downloaded for ${order.name}`, 'success');
    } catch (err) {
      showNotification(`Invoice download failed: ${err.message}`, 'error');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const resendFailedEmail = async (order, log, idx) => {
    const key = `${order.id}-${idx}-${log.created_at || ''}`;
    setResendingEmailKey(key);
    try {
      const orderDetails = buildOrderEmailDetails(order);
      const sendResult = await emailService.sendStatusUpdateEmail(
        log.status || order.custom_status,
        orderDetails,
        order.tracking_number || ''
      );
      await databaseAPI.logEmail(
        order.id,
        log.email_type || 'status_update',
        log.status || order.custom_status,
        log.recipient_email || order.email,
        log.subject || `Order ${order.name} - Status Update`,
        'sent',
        '',
        sendResult?.messageId || ''
      );
      showNotification(`✅ Email resent to ${log.recipient_email || order.email}`, 'success');
      await fetchOrders();
      const detailRes = await databaseAPI.getOrderDashboardDetail(order.id);
      if (detailRes.success && detailRes.order) {
        mergeOrderDetail(detailRes.order);
        setEmailHistoryOrder((prev) => (
          prev && prev.id === order.id ? detailRes.order : prev
        ));
      }
    } catch (err) {
      await databaseAPI.logEmail(
        order.id,
        log.email_type || 'status_update',
        log.status || order.custom_status,
        log.recipient_email || order.email,
        log.subject || `Order ${order.name} - Status Update`,
        'failed',
        err?.message || 'Resend failed'
      );
      showNotification(`❌ Resend failed: ${err?.message || 'Unknown error'}`, 'error');
    } finally {
      setResendingEmailKey('');
    }
  };

  // Check if all selected orders are from India
  const areAllSelectedOrdersIndian = () => {
    if (selectedOrders.size === 0) return false;
    
    const selectedOrdersList = Array.from(selectedOrders);
    const selectedOrdersData = orders.filter(order => selectedOrdersList.includes(order.id));
    
    return selectedOrdersData.every(order => isIndianOrder(order));
  };

  // Bulk selection functions
  const handleSelectOrder = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
    setSelectAll(newSelected.size === orders.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders(new Set());
      setSelectAll(false);
    } else {
      const allOrderIds = new Set(orders.map(order => order.id));
      setSelectedOrders(allOrderIds);
      setSelectAll(true);
    }
  };

  const clearSelection = () => {
    setSelectedOrders(new Set());
    setSelectAll(false);
    setBulkActionType('');
    setShowBulkActions(false);
  };

  // Bulk action handler
  const handleBulkAction = async (actionType) => {
    if (selectedOrders.size === 0) {
      alert('⚠️ Please select at least one order first!');
      return;
    }

    const selectedOrdersList = Array.from(selectedOrders);
    const confirm = await showConfirm({
      title: 'Confirm Bulk Update',
      message: `Are you sure you want to apply "${actionType}" to ${selectedOrdersList.length} selected orders?`,
      confirmText: 'Apply',
      cancelText: 'Cancel'
    });

    if (!confirm) return;

    // Here you can implement specific bulk actions
    
    // Example: Bulk status update
    if (actionType.startsWith('status_')) {
      const newStatus = actionType.replace('status_', '');
      await handleBulkStatusUpdate(selectedOrdersList, newStatus);
    }
  };

  const handleBulkStatusUpdate = async (orderIds, newStatus) => {
    let successCount = 0;
    let errorCount = 0;

    for (const orderId of orderIds) {
      try {
        const order = orders.find(o => o.id === orderId);
        if (order && isStatusChangeAllowed(order.custom_status, newStatus, order.is_preorder)) {
          await updateOrderStatus(orderId, newStatus);
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`Error updating order ${orderId}:`, error);
        errorCount++;
      }
    }

    alert(`✅ Bulk update completed!\nSuccess: ${successCount}\nSkipped/Failed: ${errorCount}`);
    clearSelection();
  };

  // CSV Upload Functions
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const isValidFile = fileName.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';

      if (isValidFile) {
        setCsvFile(file);
        parseCsvFile(file);
      } else {
        alert(`⚠️ Invalid file type!\nSelected: ${file.type || 'Unknown'}\nPlease select a CSV file (.csv extension)`);
      }
    }
  };

  const parseCsvFile = (file) => {
    const reader = new FileReader();

    reader.onerror = () => {
      alert('❌ Error reading file! Please try again or check if file is corrupted.');
    };

    reader.onload = (e) => {
      const { rows, error, ignoredCount } = parseStatusCsvText(e.target.result);
      if (error) {
        alert(error);
        return;
      }
      setCsvData(rows);
      if (rows.length === 0) {
        alert('⚠️ No valid data rows found in CSV!\nMake sure your CSV has data in Order ID and Status columns.');
      } else {
        alert(`✅ CSV loaded successfully!\n📊 Found ${rows.length} valid orders\n🔍 Ignored ${ignoredCount} invalid/empty rows`);
      }
    };

    reader.readAsText(file, 'UTF-8');
  };

  const processCsvData = async () => {
    if (csvData.length === 0) {
      alert('⚠️ No valid CSV data found!');
      return;
    }
    setCsvProcessing(true);
    try {
      const resolveOrders = async (orderId) => {
        const q = String(orderId || '').trim().replace(/^#/, '');
        if (!q) return [];
        const res = await databaseAPI.getOrdersPage({
          orderScope: 'all',
          limit: 25,
          offset: 0,
          q,
        });
        return res.orders || [];
      };
      const results = await processStatusCsvData({
        rows: csvData,
        orders,
        updateOrderStatus,
        resolveOrders,
        mapCsvStatusToSystem,
        isStatusChangeAllowed,
        getStatusMapping,
        alert,
      });
      if (results) setCsvResults(results);
      // Refresh list so updated statuses show after CSV
      try {
        await fetchOrders(orderScope, { reset: true, silent: true });
      } catch (_) {
        /* ignore refresh errors */
      }
    } finally {
      setCsvProcessing(false);
    }
  };

  const resetCsvUpload = () => {
    setCsvFile(null);
    setCsvData([]);
    setCsvResults(null);
    setShowCsvUpload(false);
  };

  // ===== CSV Create Orders: Handlers =====
  const handleOrderCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();
    const isValidFile = fileName.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
    if (!isValidFile) {
      alert('Please upload a valid CSV (.csv) file');
      return;
    }
    setOrderCsvFile(file);
    parseOrderCreateCsv(file);
  };

  const parseOrderCreateCsv = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const { rows, error } = parseOrderCreateCsvText(e.target.result || '');
      // Hard parse failures: do not touch state (matches previous behavior)
      if (error && rows.length === 0 && error !== 'No valid rows found. Ensure required columns are present.') {
        alert(error);
        return;
      }
      setOrderCsvData(rows);
      if (rows.length === 0) {
        alert(error || 'No valid rows found. Ensure required columns are present.');
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const processOrderCreateCsv = async () => {
    if (orderCsvData.length === 0) { alert('No CSV data to process'); return; }
    setOrderCsvProcessing(true);
    try {
      await processOrderCreateCsvRows({
        rows: orderCsvData,
        databaseAPI,
        alert,
        onEnriched: (enriched) => setOrderCsvData(enriched),
        onDone: ({ success }) => {
          if (success > 0) fetchOrders();
        },
      });
    } finally {
      setOrderCsvProcessing(false);
    }
  };

  const downloadOrderCreateSampleCsv = () => downloadOrderCreateSampleCsvFile();

  const downloadSampleCsv = () => downloadStatusSampleCsv(alert);

  useEffect(() => {
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Refetch from DB when filters/scope change (no Shopify sync — sync runs in background).
  // After the first load, keep the dashboard mounted (silent) so filter changes don't flash a full-page reload.
  useEffect(() => {
    let cancelled = false;
    const silent = hasCompletedInitialFetch.current;

    (async () => {
      if (silent) setIsFilterRefreshing(true);
      try {
        await fetchOrders(orderScope, { reset: true, silent });
      } finally {
        if (!cancelled) {
          hasCompletedInitialFetch.current = true;
          setIsFilterRefreshing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, debouncedSearch, dateFilter, customStartDate, customEndDate, selectedType, selectedPaymentStatus, statusPreset, orderScope]);

  // Background: every 2 minutes sync Shopify → DB, then soft-refresh current list
  useEffect(() => {
    const tick = async () => {
      try {
        setBackgroundSyncing(true);
        await databaseAPI.fastSyncOrdersFromShopify();
        await fetchOrders(orderScope, { reset: true, silent: true });
      } catch (_e) {
        // ignore background failures
      } finally {
        setBackgroundSyncing(false);
      }
    };
    const id = setInterval(tick, 2 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderScope, selectedStatus, debouncedSearch, dateFilter, customStartDate, customEndDate, selectedType, selectedPaymentStatus, statusPreset]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const status = params.get('status');
    const type = params.get('type');
    const date = params.get('date');
    const preset = params.get('preset');

    if (status) setSelectedStatus(status);
    if (type) setSelectedType(type);
    const payment = params.get('payment');
    if (payment) setSelectedPaymentStatus(payment);
    if (date) setDateFilter(date);
    if (preset) {
      setStatusPreset(preset);
      setSelectedStatus('all');
    } else if (!status) {
      setStatusPreset('none');
    }
  }, [location.search]);

  useEffect(() => {
    emailService.setServiceType('backend');
  }, []);

  useEffect(() => {
    if (!showDisputeView) return;
    const needPhotos = orders.filter(
      (o) => o.dispute_data
        && (o.dispute_data.photo_count > 0)
        && !(Array.isArray(o.dispute_data.photos) && o.dispute_data.photos.length),
    );
    if (!needPhotos.length) return;

    (async () => {
      const res = await databaseAPI.getOrdersDashboardDetails(needPhotos.map((o) => o.id));
      if (!res.success || !res.orders?.length) return;
      const byId = Object.fromEntries(res.orders.map((o) => [o.id, o]));
      setOrders((prev) => prev.map((o) => (byId[o.id] ? { ...o, ...byId[o.id] } : o)));
    })();
  }, [showDisputeView, orders]);

  // Clear selection when loaded orders change
  useEffect(() => {
    clearSelection();
  }, [orders]);

  // Update select all state when displayed orders change
  useEffect(() => {
    if (orders.length > 0) {
      const allDisplayedSelected = orders.every(order => selectedOrders.has(order.id));
      setSelectAll(allDisplayedSelected && selectedOrders.size > 0);
    }
  }, [orders, selectedOrders]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportDropdown && !event.target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportDropdown]);

  // Infinite scroll — load next page from API
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      if (scrollTop + clientHeight >= scrollHeight - 100) {
        loadMoreOrders();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [nextOffset, isLoadingMore, orderScope]);

  const loadMoreOrders = () => {
    if (isLoadingMore || nextOffset === null || nextOffset === undefined || nextOffset === '') {
      return;
    }
    fetchOrders(orderScope, { reset: false, offset: nextOffset });
  };

  // Issue Reported Functions
  const openDisputeModal = (orderId) => {
    setDisputeOrderId(orderId);
    setDisputeReason('');
    setDisputeComment('');
    setDisputePhotos([]);
    setShowDisputeModal(true);
  };

  const closeDisputeModal = () => {
    setShowDisputeModal(false);
    setDisputeOrderId(null);
    setDisputeReason('');
    setDisputeComment('');
    setDisputePhotos([]);
  };

  const handleDisputePhotoUpload = (event) => {
    const files = Array.from(event.target.files);
    
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
      
      if (!isValidType) {
        alert(`❌ ${file.name} is not an image file`);
        return false;
      }
      
      if (!isValidSize) {
        alert(`❌ ${file.name} is too large (max 5MB)`);
        return false;
      }
      
      return true;
    });
    
    const combined = [...disputePhotos, ...validFiles].slice(0, 5);
    if (disputePhotos.length + validFiles.length > 5) {
      alert('⚠️ Maximum 5 photos allowed. Extra files were ignored.');
    }
    setDisputePhotos(combined);
  };

  const submitDispute = async () => {
    if (!disputeReason.trim()) {
      alert('⚠️ Please select a dispute reason');
      return;
    }
    if (!disputeComment.trim()) {
      alert('⚠️ Please add a comment describing the issue');
      return;
    }
    if (!disputePhotos.length) {
      alert('⚠️ Please upload at least one issue image');
      return;
    }

    setProcessingDispute(true);
    try {
      await submitDisputeAction({
        disputeOrderId,
        disputeReason,
        disputeComment,
        disputePhotos,
        orders,
        alert,
        onClose: closeDisputeModal,
        onRefresh: fetchOrders,
        api: databaseAPI,
        mailer: emailService,
        skipValidation: true,
      });
    } finally {
      setProcessingDispute(false);
    }
  };

  const resolveDispute = async (orderId, resolution) => {
    await resolveDisputeAction({
      orderId,
      resolution,
      orders,
      showConfirm,
      showNotification,
      onRefresh: fetchOrders,
      api: databaseAPI,
      mailer: emailService,
    });
  };

  const getDisputeData = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    return order?.dispute_data || null;
  };

  const getFilterDateRange = () => {
    const now = new Date();
    const pad = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    if (dateFilter === 'all') return { dateFrom: '', dateTo: '' };
    if (dateFilter === 'custom') {
      return { dateFrom: customStartDate || '', dateTo: customEndDate || '' };
    }
    if (dateFilter === 'today') {
      const t = pad(now);
      return { dateFrom: t, dateTo: t };
    }
    let start = new Date(now);
    if (dateFilter === '7days') start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    else if (dateFilter === '1month') start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    else if (dateFilter === '3months') start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    return { dateFrom: pad(start), dateTo: pad(now) };
  };

  const normalizeOrderRow = (order) => ({
    ...order,
    name: order.name,
    email: order.email,
    customer: order.customer,
    total_price: order.total_price,
    currency: order.currency,
    line_items: order.line_items,
    shipping_address: order.shipping_address,
    created_at: order.created_at,
    custom_status: order.custom_status,
    tracking_number: order.tracking_number || '',
    is_preorder: order.is_preorder,
    dispute_data: order.dispute_data,
    email_status: order.email_status || {},
    email_logs: order.email_logs || [],
    invoice_url: order.invoice_url || null,
  });

  const fetchOrders = async (scopeOverride = null, { reset = true, offset = 0, silent = false } = {}) => {
    try {
      if (reset && !silent) setLoading(true);
      if (!reset) setIsLoadingMore(true);
      setError(null);

      const scopeToUse = scopeOverride || orderScope;
      const { dateFrom, dateTo } = getFilterDateRange();
      const preorderActive = selectedType === 'preorder';

      const dbResult = await databaseAPI.getOrdersPage({
        orderScope: scopeToUse,
        limit: ordersPerPage,
        offset: reset ? 0 : offset,
        status: statusPreset !== 'none' ? 'all' : selectedStatus,
        statusPreset,
        orderType: selectedType === 'preorder' ? 'preorder' : selectedType === 'regular' ? 'regular' : 'all',
        paymentStatus: selectedPaymentStatus,
        q: debouncedSearch,
        dateFrom,
        dateTo,
        preorderActive,
      });

      if (!dbResult.success) {
        if (!silent) setError('Failed to load orders from database: ' + dbResult.error);
        return;
      }

      const pageOrders = (dbResult.orders || []).map(normalizeOrderRow);
      setTotalMatching(dbResult.total || 0);
      setNextOffset(dbResult.next_offset ?? null);
      if (reset) setStatusCounts(dbResult.status_counts || {});

      if (reset) {
        setOrders(pageOrders);
      } else {
        setOrders((prev) => {
          const seen = new Set(prev.map((o) => o.id));
          const merged = [...prev];
          for (const o of pageOrders) {
            if (!seen.has(o.id)) merged.push(o);
          }
          return merged;
        });
      }
      setError(null);
    } catch (err) {
      console.error('❌ Database fetch error:', err);
      if (!silent) setError('Failed to fetch orders: ' + err.message);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Update displayed orders — show all loaded server pages
  const updateOrderStatus = async (orderId, newStatus, trackingNumber = '', orderOverride = null) => {
    try {
      setUpdatingOrder(orderId);
      
      const order = orderOverride || orders.find(o => o.id === orderId);
      if (!order) {
        console.error(`Order not found: ${orderId}`);
        return { success: false, error: 'Order not found' };
      }

      // Check if status change is allowed
      if (!isStatusChangeAllowed(order.custom_status, newStatus, order.is_preorder)) {
        const statusMapping = getStatusMapping(order.is_preorder);
        const errorMsg = `Status cannot be changed backward! Current: ${statusMapping[order.custom_status]}, Attempted: ${statusMapping[newStatus]}`;
        showNotification(
          `❌ ${errorMsg}`,
          'error',
          4000
        );
        return { success: false, error: errorMsg };
      }

      // Update status in database
      const updateResult = await databaseAPI.updateOrderStatus(orderId, newStatus, trackingNumber);
      if (!updateResult.success) {
        const errorMsg = `Failed to update order status: ${updateResult.error}`;
        showNotification(
          `❌ ${errorMsg}`,
          'error',
          5000
        );
        return { success: false, error: errorMsg };
      }
      

      // Update local state
      let updatedOrder = { 
        ...order, 
        custom_status: newStatus, 
        tracking_number: trackingNumber || order.tracking_number 
      };
      
      // Convert preorder to regular when moving to advanced statuses
      if (order.is_preorder && (newStatus === 'stock_in' || newStatus === 'packing' || newStatus === 'shipping_domestic' || newStatus === 'shipping_international' || newStatus === 'arrived_at_destination' || newStatus === 'out_of_delivery' || newStatus === 'destoryed' || newStatus === 'delivered')) {
        updatedOrder.is_preorder = false;
      }

      // Update orders state (merge in if resolved from API and not yet loaded)
      const updatedOrders = (() => {
        const mapped = orders.map(o => (o.id === orderId ? updatedOrder : o));
        if (orders.some(o => o.id === orderId)) return mapped;
        return [updatedOrder, ...mapped];
      })();
      setOrders(updatedOrders);

      // Handle Shopify fulfillment for 'shipping' status
      if (newStatus === 'shipping_domestic' || newStatus === 'shipping_international') {
        try {
          await shopifyAPI.createFulfillment(orderId, trackingNumber, 'Custom Tracking');
        } catch (fulfillmentError) {
          console.error('Fulfillment error:', fulfillmentError);
          // Continue even if fulfillment fails
        }
      }

      // Send email notification
      try {
        const orderDetails = {
          email: order.email,
          name: order.customer ? `${order.customer.first_name} ${order.customer.last_name}` : '',
          order_name: order.name,
          line_items: order.line_items
        };
        
        await emailService.sendStatusUpdateEmail(newStatus, orderDetails, trackingNumber, updatedOrder.is_preorder);
        
        // Log email success in database
        await databaseAPI.logEmail(
          orderId,
          'status_update',
          newStatus,
          order.email,
          `Order ${order.name} Status Update`,
          'sent'
        );
        
        // Show conversion message if order was converted
        const conversionMessage = (order.is_preorder && (newStatus === 'stock_in' || newStatus === 'packing' || newStatus === 'shipping_domestic' || newStatus === 'shipping_international' || newStatus === 'arrived_at_destination' || newStatus === 'out_of_delivery' || newStatus === 'destoryed' || newStatus === 'delivered')) 
          ? '\n🔄 Order converted from preorder to regular order!'
          : '';
        
        showNotification(
          `✅ Send\nOrder ${order.name} - Email sent to ${order.email}${conversionMessage}`,
          'success',
          4000
        );
        
        return { success: true, message: 'Order status updated successfully' };
      } catch (emailError) {
        console.error('Email error:', emailError);
        
        // Log email failure in database
        await databaseAPI.logEmail(
          orderId,
          'status_update',
          newStatus,
          order.email,
          `Order ${order.name} Status Update`,
          'failed',
          emailError.message
        );
        
        // Show conversion message if order was converted
        const conversionMessage = (order.is_preorder && (newStatus === 'stock_in' || newStatus === 'packing' || newStatus === 'shipping_domestic' || newStatus === 'shipping_international' || newStatus === 'arrived_at_destination' || newStatus === 'out_of_delivery' || newStatus === 'destoryed' || newStatus === 'delivered')) 
          ? '\n🔄 Order converted from preorder to regular order!'
          : '';
        
        showNotification(
          `❌ Fail\nOrder ${order.name} - Email failed to ${order.email}${conversionMessage}`,
          'error',
          5000
        );
        
        return { success: false, error: emailError.message };
      }

    } catch (error) {
      console.error('Error updating order status:', error);
      showNotification(
        'Failed to update order status: ' + error.message,
        'error',
        5000
      );
      return { success: false, error: error.message };
    } finally {
      setUpdatingOrder(null);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) {
        console.error(`Order ${orderId} not found.`);
        return;
    }

    // Special handling for dispute status - open dispute modal instead
    if (newStatus === 'dispute') {
        openDisputeModal(orderId);
        return;
    }

    // 🔄 AUTO-CORRECTION: Only fix Indian orders mistakenly set to international
    let correctedStatus = newStatus;
    let correctionMessage = '';

    if (newStatus === 'shipping_international') {
      const isIndian = isIndianOrder(order);
      
      if (isIndian) {
        // Indian order mistakenly set to international - auto-correct to domestic
        correctedStatus = 'shipping_domestic';
        correctionMessage = '\n\n🔄 Auto-corrected: Indian order changed to Domestic Shipping (Indian orders stay within India)';
      }
    }
    
    // ✅ Allow International orders to use both Domestic and International shipping
    // This allows: USA order → Shipped Domestic (India to Delhi) → Shipped International (Delhi to USA)

    // Confirmation for status changes
    const statusToDisplay = correctedStatus === newStatus ? 
      customStatusMap[newStatus] : 
      `${customStatusMap[correctedStatus]} (Auto-corrected from ${customStatusMap[newStatus]})`;
      
    const confirmChange = await showConfirm({
      title: 'Confirm Status Change',
      message: `Are you sure you want to change order ${order.name} status to "${statusToDisplay}"?${correctionMessage}`,
      confirmText: 'Confirm',
      cancelText: 'Cancel'
    });
    
    if (!confirmChange) return;

    // Handle tracking ID for shipping and post-shipping milestones (emails include Speedbox link)
    if (correctedStatus === 'shipping_domestic' || correctedStatus === 'shipping_international' || correctedStatus === 'destoryed' || correctedStatus === 'arrived_at_destination' || correctedStatus === 'out_of_delivery') {
      const isIndian = isIndianOrder(order);
      
      let trackingPrompt;
      if (correctedStatus === 'shipping_domestic') {
        trackingPrompt = isIndian ? 
          'Enter DOMESTIC tracking number (within India):' : 
          'Enter DOMESTIC tracking number (India to Delhi Airport):';
      } else if (correctedStatus === 'shipping_international') {
        trackingPrompt = 'Enter INTERNATIONAL tracking number (Delhi to destination country):';
      } else {
        trackingPrompt = 'Enter tracking number for this update (recommended — used in the customer email):';
      }
        
      const trackingNumber = await showPrompt({
        title: 'Enter Tracking Number',
        message: trackingPrompt,
        confirmText: 'Continue',
        cancelText: 'Cancel'
      });
      if (trackingNumber === null) return; // User cancelled
      
      if (trackingNumber.trim() === '') {
        const confirmWithoutTracking = await showConfirm({
          title: 'No Tracking Number',
          message: 'Are you sure you want to change status to Shipping without tracking number?',
          confirmText: 'Yes, Continue',
          cancelText: 'Go Back'
        });
        if (!confirmWithoutTracking) return;
      } else {
        // Confirm tracking number
        const confirmTracking = await showConfirm({
          title: 'Confirm Tracking',
          message: `Confirm shipping tracking number:\n${trackingNumber}\n\nIs this correct?`,
          confirmText: 'Looks Correct',
          cancelText: 'Edit'
        });
        if (!confirmTracking) return;
      }
      
      updateOrderStatus(orderId, correctedStatus, trackingNumber.trim());
    } 
    else {
      // For all other statuses (except dispute) - no tracking needed
      updateOrderStatus(orderId, correctedStatus);
    }
  };

  // Check if order is from India (domestic)
  const isIndianOrder = (order) => {
    if (!order.shipping_address && !order.billing_address) return true; // Default to domestic
    
    const shippingCountry = order.shipping_address?.country?.toLowerCase();
    const billingCountry = order.billing_address?.country?.toLowerCase();
    
    // Check if either shipping or billing address is India
    return shippingCountry === 'india' || shippingCountry === 'in' || 
           billingCountry === 'india' || billingCountry === 'in' ||
           shippingCountry === 'भारत'; // Hindi name for India
  };

  // Check if there are any non-Indian orders in current filtered orders
  const hasNonIndianOrders = () => {
    return orders.some(order => !isIndianOrder(order));
  };

  // Get available shipping options based on order location
  const getAvailableShippingOptions = (order) => {
    const isIndia = isIndianOrder(order);
    
    if (isIndia) {
      // India orders: Only domestic shipping
      return {
        showDomestic: true,
        showInternational: false
      };
    } else {
      // International orders: Both options available
      return {
        showDomestic: true,
        showInternational: true
      };
    }
  };

  // Export: fetch ALL matching filtered orders (not just the loaded page of 50)
  const buildExportFilters = (scope) => {
    const { dateFrom, dateTo } = getFilterDateRange();
    return {
      orderScope: scope,
      status: statusPreset !== 'none' ? 'all' : selectedStatus,
      statusPreset,
      orderType: selectedType === 'preorder' ? 'preorder' : selectedType === 'regular' ? 'regular' : 'all',
      paymentStatus: selectedPaymentStatus,
      q: debouncedSearch,
      dateFrom,
      dateTo,
      preorderActive: selectedType === 'preorder',
    };
  };

  const runExport = async (format, scope = exportScope) => {
    if (exporting) return;
    setExporting(true);
    try {
      const filters = buildExportFilters(scope);
      await downloadOrdersExportFromServer(databaseAPI, {
        orderScope: filters.orderScope,
        status: filters.status,
        statusPreset: filters.statusPreset,
        orderType: filters.orderType,
        paymentStatus: filters.paymentStatus,
        q: filters.q,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        preorderActive: filters.preorderActive,
      }, format);
      alert('✅ Export downloaded (server-side).');
    } catch (err) {
      alert(`❌ Export failed: ${err.message || err}`);
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = (scope = exportScope) => runExport('csv', scope);
  const exportToExcel = (scope = exportScope) => runExport('excel', scope);

  // Email content for review
  const getReviewEmailContent = (productUrl) => {
    return `
      <div style="font-family: Arial, sans-serif;">
        <h2>We hope you're loving your purchase!</h2>
        <p>We'd love to hear your thoughts on the product you purchased. Your feedback helps us improve and serve you better.</p>
        <p>Click the link below to leave a review:</p>
        <a href="${productUrl}" style="display: inline-block; padding: 10px 20px; background-color: #4CAF50; color: white; text-decoration: none; border-radius: 5px;">Leave a Review</a>
        <p>Thank you for being a valued customer!</p>
      </div>
    `;
  };

  if (loading && !hasCompletedInitialFetch.current) {
    return (
      <div className="order-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading ALL orders from Shopify store...</p>
          <p className="loading-subtext">Fetching from database - This may take a few moments</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-dashboard">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchOrders} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-dashboard">
      {showSubscriptionSheet ? (
        <div className="subscription-fullview">
          <div className="subscription-fullview-header">
            <button
              type="button"
              onClick={() => setShowSubscriptionSheet(false)}
              className="subscription-fullview-back-btn"
            >
              ← Back to Orders
            </button>
            <h2 className="subscription-fullview-title">Subscription Order Tracker</h2>
          </div>
          <div className="subscription-fullview-iframe">
            <iframe
              src="https://docs.google.com/spreadsheets/d/1TPS75-S8Cnyg0TD3TvJm5pFVzboB2whioQ_VmqGaEnA/edit?gid=1525964514#gid=1525964514"
              title="Shopify Subscription Tracker"
              className="subscription-fullview-iframe-el"
            />
          </div>
        </div>
      ) : (
        <>
      <OrderDashboardHeader
        orderScope={orderScope}
        onScopeChange={(scope) => {
          if (orderScope === scope) return;
          setOrderScope(scope);
          setSelectedOrders(new Set());
          setSelectAll(false);
        }}
        totalMatching={totalMatching}
        loadedCount={orders.length}
        backgroundSyncing={backgroundSyncing}
        loading={loading}
        fetchOrders={fetchOrders}
        onSyncNow={async () => {
          setBackgroundSyncing(true);
          try {
            await databaseAPI.fastSyncOrdersFromShopify();
            await fetchOrders(orderScope, { reset: true });
          } finally {
            setBackgroundSyncing(false);
          }
        }}
        onShowAddOrder={() => setShowAddOrderModal(true)}
        showCsvUpload={showCsvUpload}
        setShowCsvUpload={setShowCsvUpload}
        showExportDropdown={showExportDropdown}
        setShowExportDropdown={setShowExportDropdown}
        exportScope={exportScope}
        setExportScope={setExportScope}
        exporting={exporting}
        exportToCSV={exportToCSV}
        exportToExcel={exportToExcel}
        onShowSubscription={() => setShowSubscriptionSheet(true)}
        onClearCache={() => {
          shopifyAPI.clearCache();
          const keys = Object.keys(localStorage);
          keys.forEach((key) => {
            if (
              key.includes('email_sent_') ||
              key.includes('email_failed_') ||
              key.includes('email_error_') ||
              key.includes('email_timestamp_')
            ) {
              localStorage.removeItem(key);
            }
          });
          fetchOrders();
        }}
      />

      <StatusCsvUploadSection
        showCsvUpload={showCsvUpload}
        csvFile={csvFile}
        csvData={csvData}
        csvProcessing={csvProcessing}
        csvResults={csvResults}
        handleCsvFileChange={handleCsvFileChange}
        downloadSampleCsv={downloadSampleCsv}
        processCsvData={processCsvData}
        resetCsvUpload={resetCsvUpload}
      />

      <OrderFiltersBar
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedPaymentStatus={selectedPaymentStatus}
        setSelectedPaymentStatus={setSelectedPaymentStatus}
        dateFilter={dateFilter}
        setDateFilter={setDateFilter}
        customStartDate={customStartDate}
        setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}
        setCustomEndDate={setCustomEndDate}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        setStatusPreset={setStatusPreset}
        hasNonIndianOrders={hasNonIndianOrders}
        showDisputeView={showDisputeView}
        setShowDisputeView={setShowDisputeView}
      />

      <OrderDashboardStats
        totalMatching={totalMatching}
        dateFilter={dateFilter}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        statusCounts={statusCounts}
        hasNonIndianOrders={hasNonIndianOrders}
      />

      {showDisputeView && (
        <DisputeManagementPanel orders={orders} onResolveDispute={resolveDispute} />
      )}

      {/* Only show orders table when not in dispute view */}
      {!showDisputeView && (
        <>
      <AddOrderModal
        show={showAddOrderModal}
        onClose={() => setShowAddOrderModal(false)}
        addOrderMode={addOrderMode}
        setAddOrderMode={setAddOrderMode}
        newOrder={newOrder}
        setNewOrder={setNewOrder}
        billingSameAsShipping={billingSameAsShipping}
        setBillingSameAsShipping={setBillingSameAsShipping}
        orderCsvData={orderCsvData}
        setOrderCsvFile={setOrderCsvFile}
        setOrderCsvData={setOrderCsvData}
        orderCsvProcessing={orderCsvProcessing}
        handleOrderCsvFileChange={handleOrderCsvFileChange}
        downloadOrderCreateSampleCsv={downloadOrderCreateSampleCsv}
        processOrderCreateCsv={processOrderCreateCsv}
        fetchOrders={fetchOrders}
      />
      {/* Bulk Actions Bar */}
      {selectedOrders.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-info">
            <span className="selected-count">
              {selectedOrders.size} orders selected
            </span>
            <button onClick={clearSelection} className="clear-selection-btn">
              ✕ Clear Selection
            </button>
          </div>
          
          <div className="bulk-actions">
            <select 
              value={bulkActionType} 
              onChange={(e) => setBulkActionType(e.target.value)}
              className="bulk-action-select"
            >
              <option value="">Select Bulk Action</option>
              <option value="status_preorder_received">🔮 Set to Preorder Placed</option>
              <option value="status_progress">⚙️ Set to Production Update</option>
              <option value="status_delay">⏳ Set to Delay Update</option>
              <option value="status_stock_in">📦 Set to Stock Arrived</option>
              <option value="status_packing">📦 Set to Packing Started</option>
              <option value="status_shipping_domestic">🚚 Set to Shipped Domestic</option>
              {/* Show International Shipping only if there are non-Indian orders */}
              {hasNonIndianOrders() && (
                <option value="status_shipping_international">✈️ Set to Shipped International</option>
              )}
              <option value="status_destoryed">✅ Set to Customs Cleared</option>
              <option value="status_arrived_at_destination">📍 Set to Arrived in Destination Country</option>
              <option value="status_out_of_delivery">🚛 Set to Out for Delivery</option>
                  <option value="status_delivered">✅ Set to Delivered</option>
            </select>
            
            <button 
              onClick={() => handleBulkAction(bulkActionType)}
              disabled={!bulkActionType}
              className="apply-bulk-btn"
            >
              Apply Action
            </button>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className={`orders-container${isFilterRefreshing ? ' is-filter-refreshing' : ''}`}>
        {isFilterRefreshing && (
          <div className="filter-refresh-banner" aria-live="polite">
            Updating orders…
          </div>
        )}
        <div className="table-responsive">
          <table className="orders-table">
            <colgroup>
              <col className="col-select" />
              <col className="col-order-id" />
              <col className="col-payment" />
              <col className="col-customer" />
              <col className="col-email" />
              <col className="col-items" />
              <col className="col-qty" />
              <col className="col-total" />
              <col className="col-shipping" />
              <col className="col-billing" />
              <col className="col-date" />
              <col className="col-status" />
              <col className="col-tracking" />
              <col className="col-actions" />
              <col className="col-invoice" />
            </colgroup>
            <thead>
              <tr>
                <th className="select-column">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    className="select-all-checkbox"
                  />
                </th>
                <th>Order ID</th>
                <th>Payment</th>
                <th>Customer</th>
                <th>Email</th>
                <th>Items</th>
                <th>Qty</th>
                <th>Total</th>
                <th>Shipping Address</th>
                <th>Billing Address</th>
                <th>Date</th>
                <th>Status</th>
                <th>Tracking</th>
                <th className="actions-col">Actions</th>
                <th className="invoice-col">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id} className={selectedOrders.has(order.id) ? 'selected-row' : ''}>
                  <td className="select-column">
                    <input
                      type="checkbox"
                      checked={selectedOrders.has(order.id)}
                      onChange={() => handleSelectOrder(order.id)}
                      className="order-checkbox"
                    />
                  </td>
                  <td className="order-id">
                    {order.name}
                    {order.is_preorder && (
                      <span className="preorder-badge" title="This is a preorder">
                        🔮
                      </span>
                    )}
                  </td>
                  <td className="payment-status">
                    <span className={`payment-status-badge ${paymentStatusClass(order)}`}>
                      {formatPaymentStatus(order)}
                    </span>
                  </td>
                      <td className="customer-name" style={{fontSize: '10px'}}>
                    {order.customer 
                      ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim()
                      : 'Guest'
                    }
                  </td>
                      <td className="customer-email" style={{fontSize: '10px'}}>{order.email}</td>
                  <td className="order-items">
                    <div className="items-list">
                      {order.line_items.map((item, index) => {
                        const productId = item.product_id;
                        const productImage = productImagesCache[productId] || productImagesCache[String(productId)] || null;
                        return (
                          <div key={index} className="item" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {productImage ? (
                              <img 
                                src={productImage} 
                                alt={item.title}
                                className="product-thumbnail"
                                onClick={() => setImageModal({ 
                                  show: true, 
                                  imageUrl: productImage, 
                                  productTitle: item.title 
                                })}
                                style={{ 
                                  width: '32px', 
                                  height: '32px', 
                                  objectFit: 'cover', 
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  border: '1px solid #e2e8f0'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : productId ? (
                              <div 
                                className="product-thumbnail-placeholder"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  backgroundColor: '#f1f5f9',
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                  fontSize: '10px',
                                  color: '#64748b'
                                }}
                              >
                                📦
                              </div>
                            ) : null}
                            <div style={{ flex: 1 }}>
                              <strong style={{fontSize: '8px'}}>{item.title}</strong>
                              <span className="item-details" style={{fontSize: '8px', display: 'block'}}>
                                Qty: {item.quantity} - ${item.price}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td className="order-qty" style={{whiteSpace: 'nowrap'}}>
                    {order.line_items && order.line_items.length > 0
                      ? order.line_items.reduce((sum, it) => sum + (parseInt(it.quantity || 0, 10)), 0)
                      : 0}
                  </td>
                  <td className="order-total">
                    ${order.total_price} {order.currency}
                  </td>
                  <td className="shipping-address">
                        <div className="address-tooltip" style={{fontSize: '8px'}}>
                      <strong>📦 Shipping:</strong><br/>
                      {formatAddress(order.shipping_address)}
                    </div>
                  </td>
                  <td className="billing-address">
                        <div className="address-tooltip" style={{fontSize: '8px'}}>
                      <strong>💳 Billing:</strong><br/>
                      {formatAddress(order.billing_address)}
                    </div>
                  </td>
                  <td className="order-date">
                    {formatDate(order.created_at)}
                  </td>
                  <td className="order-status">
                    <span 
                      className="status-badge"
                          style={{ backgroundColor: getStatusColor(order.custom_status), fontSize: '8px' }}
                    >
                      {getStatusMapping(order.is_preorder)[order.custom_status] || order.custom_status}
                    </span>
                  </td>
                  <td className="tracking-info">
                        {order.tracking_number ? (
                      <div className="tracking-number">
                        <span className="tracking-label">🚚</span>
                        <span className="tracking-id">
                              {order.tracking_number}
                        </span>
                      </div>
                    ) : (
                      <span className="no-tracking">-</span>
                    )}
                  </td>
                  <td className="order-actions">
                    <div className="action-buttons">
                      <select
                        value={order.custom_status}
                            onChange={(e) => {
                              // Special handling for dispute resolution
                              if (order.custom_status === 'dispute' && e.target.value === 'replacement_approved') {
                                resolveDispute(order.id, 'replacement');
                              } else if (order.custom_status === 'dispute' && e.target.value === 'refunded') {
                                resolveDispute(order.id, 'refund');
                              } else {
                                handleStatusChange(order.id, e.target.value);
                              }
                            }}
                        disabled={updatingOrder === order.id}
                        className="status-select"
                      >
                        {/* Show different options based on order type */}
                        {order.is_preorder ? (
                          // Preorder options
                          <>
                            <option 
                              value="preorder_received" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'preorder_received', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'preorder_received', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              🔮 Preorder Placed {!isStatusChangeAllowed(order.custom_status, 'preorder_received', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option 
                              value="progress" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'progress', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'progress', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              ⚙️ Production Update {!isStatusChangeAllowed(order.custom_status, 'progress', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option 
                              value="delay" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'delay', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'delay', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              ⏳ Delay Update {!isStatusChangeAllowed(order.custom_status, 'delay', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option 
                              value="stock_in" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'stock_in', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'stock_in', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              📦 Stock Arrived {!isStatusChangeAllowed(order.custom_status, 'stock_in', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option 
                              value="packing" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'packing', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'packing', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              📦 Packing Started {!isStatusChangeAllowed(order.custom_status, 'packing', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option 
                              value="shipping_domestic" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'shipping_domestic', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'shipping_domestic', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              🚚 Shipped Domestic {!isStatusChangeAllowed(order.custom_status, 'shipping_domestic', order.is_preorder) ? '🚫' : ''}
                            </option>
                            {/* Show International Shipping only for non-Indian orders */}
                            {!isIndianOrder(order) && (
                              <option 
                                value="shipping_international" 
                                disabled={!isStatusChangeAllowed(order.custom_status, 'shipping_international', order.is_preorder)}
                                style={{ 
                                  color: !isStatusChangeAllowed(order.custom_status, 'shipping_international', order.is_preorder) ? '#ccc' : 'inherit'
                                }}
                              >
                                ✈️ Shipped International {!isStatusChangeAllowed(order.custom_status, 'shipping_international', order.is_preorder) ? '🚫' : ''}
                              </option>
                            )}
                            <option
                              value="arrived_at_destination"
                              disabled={!isStatusChangeAllowed(order.custom_status, 'arrived_at_destination', order.is_preorder)}
                              style={{
                                color: !isStatusChangeAllowed(order.custom_status, 'arrived_at_destination', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              📍 Arrived in Destination Country {!isStatusChangeAllowed(order.custom_status, 'arrived_at_destination', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option
                              value="out_of_delivery"
                              disabled={!isStatusChangeAllowed(order.custom_status, 'out_of_delivery', order.is_preorder)}
                              style={{
                                color: !isStatusChangeAllowed(order.custom_status, 'out_of_delivery', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              🚛 Out for Delivery {!isStatusChangeAllowed(order.custom_status, 'out_of_delivery', order.is_preorder) ? '🚫' : ''}
                            </option>
                            {!isIndianOrder(order) && (
                              <option
                                value="destoryed"
                                disabled={!isStatusChangeAllowed(order.custom_status, 'destoryed', order.is_preorder)}
                                style={{
                                  color: !isStatusChangeAllowed(order.custom_status, 'destoryed', order.is_preorder) ? '#ccc' : 'inherit'
                                }}
                              >
                                ✅ Customs Cleared {!isStatusChangeAllowed(order.custom_status, 'destoryed', order.is_preorder) ? '🚫' : ''}
                              </option>
                            )}
                            <option 
                                  value="delivered" 
                                  disabled={!isStatusChangeAllowed(order.custom_status, 'delivered', order.is_preorder)}
                              style={{ 
                                    color: !isStatusChangeAllowed(order.custom_status, 'delivered', order.is_preorder) ? '#ccc' : 'inherit'
                                  }}
                                >
                                  ✅ Delivered {!isStatusChangeAllowed(order.custom_status, 'delivered', order.is_preorder) ? '🚫' : ''}
                                </option>
                                <option 
                                  value="review" 
                                  disabled={!isStatusChangeAllowed(order.custom_status, 'review', order.is_preorder)}
                                  style={{ 
                                    color: !isStatusChangeAllowed(order.custom_status, 'review', order.is_preorder) ? '#ccc' : 'inherit'
                                  }}
                                >
                                  📝 Review Request {!isStatusChangeAllowed(order.custom_status, 'review', order.is_preorder) ? '🚫' : ''}
                                </option>
                                <option
                                  value="dispute"
                                  disabled={!isStatusChangeAllowed(order.custom_status, 'dispute', order.is_preorder)}
                                  style={{
                                    color: !isStatusChangeAllowed(order.custom_status, 'dispute', order.is_preorder) ? '#ccc' : 'inherit'
                                  }}
                                >
                                  🚨 Issue Reported {!isStatusChangeAllowed(order.custom_status, 'dispute', order.is_preorder) ? '🚫' : ''}
                                </option>
                                {/* Show dispute resolution options only when in dispute status */}
                                {order.custom_status === 'dispute' && (
                                  <>
                                    <option 
                                      value="refunded"
                                      style={{ color: '#27ae60', fontWeight: 'bold' }}
                                    >
                                      💰 Refund Processed (End Order)
                                    </option>
                                    <option 
                                      value="replacement_approved"
                                      style={{ color: '#3498db', fontWeight: 'bold' }}
                                    >
                                      📘 Replacement Approved
                            </option>
                                  </>
                                )}
                                {/* Show replacement journey only after replacement is approved */}
                                {isReplacementStatus(order.custom_status) && (
                                  <>
                                    <option
                                      value="replacement_approved"
                                      disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_approved', order.is_preorder)}
                                      style={{
                                        color: !isStatusChangeAllowed(order.custom_status, 'replacement_approved', order.is_preorder) ? '#ccc' : 'inherit'
                                      }}
                                    >
                                      📘 Replacement Approved {!isStatusChangeAllowed(order.custom_status, 'replacement_approved', order.is_preorder) ? '🚫' : ''}
                                    </option>
                                    <option
                                      value="replacement_packing"
                                      disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_packing', order.is_preorder)}
                                      style={{
                                        color: !isStatusChangeAllowed(order.custom_status, 'replacement_packing', order.is_preorder) ? '#ccc' : 'inherit'
                                      }}
                                    >
                                      📦 Replacement Packed {!isStatusChangeAllowed(order.custom_status, 'replacement_packing', order.is_preorder) ? '🚫' : ''}
                                    </option>
                                    <option
                                      value="replacement_shipping_domestic"
                                      disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_shipping_domestic', order.is_preorder)}
                                      style={{
                                        color: !isStatusChangeAllowed(order.custom_status, 'replacement_shipping_domestic', order.is_preorder) ? '#ccc' : 'inherit'
                                      }}
                                    >
                                      🚚 Replacement Shipped Domestic {!isStatusChangeAllowed(order.custom_status, 'replacement_shipping_domestic', order.is_preorder) ? '🚫' : ''}
                                    </option>
                                    {!isIndianOrder(order) && (
                                      <option
                                        value="replacement_shipping_international"
                                        disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_shipping_international', order.is_preorder)}
                                        style={{
                                          color: !isStatusChangeAllowed(order.custom_status, 'replacement_shipping_international', order.is_preorder) ? '#ccc' : 'inherit'
                                        }}
                                      >
                                        ✈️ Replacement Shipped International {!isStatusChangeAllowed(order.custom_status, 'replacement_shipping_international', order.is_preorder) ? '🚫' : ''}
                                      </option>
                                    )}
                                    <option
                                      value="replacement_delivered"
                                      disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_delivered', order.is_preorder)}
                                      style={{
                                        color: !isStatusChangeAllowed(order.custom_status, 'replacement_delivered', order.is_preorder) ? '#ccc' : 'inherit'
                                      }}
                                    >
                                      ✅ Replacement Delivered {!isStatusChangeAllowed(order.custom_status, 'replacement_delivered', order.is_preorder) ? '🚫' : ''}
                                    </option>
                                  </>
                                )}
                                {order.custom_status === 'refunded' && (
                                  <option value="refunded">💰 Refund Processed</option>
                                )}
                          </>
                        ) : (
                          // Regular order options
                          <>
                            <option 
                              value="unfulfilled" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'unfulfilled', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'unfulfilled', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              📋 Order Placed {!isStatusChangeAllowed(order.custom_status, 'unfulfilled', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option 
                              value="packing" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'packing', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'packing', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              📦 Packing Started {!isStatusChangeAllowed(order.custom_status, 'packing', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option 
                              value="shipping_domestic" 
                              disabled={!isStatusChangeAllowed(order.custom_status, 'shipping_domestic', order.is_preorder)}
                              style={{ 
                                color: !isStatusChangeAllowed(order.custom_status, 'shipping_domestic', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              🚚 Shipped Domestic {!isStatusChangeAllowed(order.custom_status, 'shipping_domestic', order.is_preorder) ? '🚫' : ''}
                            </option>
                            {/* Show International Shipping only for non-Indian orders */}
                            {!isIndianOrder(order) && (
                              <option 
                                value="shipping_international" 
                                disabled={!isStatusChangeAllowed(order.custom_status, 'shipping_international', order.is_preorder)}
                                style={{ 
                                  color: !isStatusChangeAllowed(order.custom_status, 'shipping_international', order.is_preorder) ? '#ccc' : 'inherit'
                                }}
                              >
                                ✈️ Shipped International {!isStatusChangeAllowed(order.custom_status, 'shipping_international', order.is_preorder) ? '🚫' : ''}
                              </option>
                            )}
                            <option
                              value="arrived_at_destination"
                              disabled={!isStatusChangeAllowed(order.custom_status, 'arrived_at_destination', order.is_preorder)}
                              style={{
                                color: !isStatusChangeAllowed(order.custom_status, 'arrived_at_destination', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              📍 Arrived in Destination Country {!isStatusChangeAllowed(order.custom_status, 'arrived_at_destination', order.is_preorder) ? '🚫' : ''}
                            </option>
                            <option
                              value="out_of_delivery"
                              disabled={!isStatusChangeAllowed(order.custom_status, 'out_of_delivery', order.is_preorder)}
                              style={{
                                color: !isStatusChangeAllowed(order.custom_status, 'out_of_delivery', order.is_preorder) ? '#ccc' : 'inherit'
                              }}
                            >
                              🚛 Out for Delivery {!isStatusChangeAllowed(order.custom_status, 'out_of_delivery', order.is_preorder) ? '🚫' : ''}
                            </option>
                            {!isIndianOrder(order) && (
                              <option
                                value="destoryed"
                                disabled={!isStatusChangeAllowed(order.custom_status, 'destoryed', order.is_preorder)}
                                style={{
                                  color: !isStatusChangeAllowed(order.custom_status, 'destoryed', order.is_preorder) ? '#ccc' : 'inherit'
                                }}
                              >
                                ✅ Customs Cleared {!isStatusChangeAllowed(order.custom_status, 'destoryed', order.is_preorder) ? '🚫' : ''}
                              </option>
                            )}
                            <option 
                                  value="delivered" 
                                  disabled={!isStatusChangeAllowed(order.custom_status, 'delivered', order.is_preorder)}
                              style={{ 
                                    color: !isStatusChangeAllowed(order.custom_status, 'delivered', order.is_preorder) ? '#ccc' : 'inherit'
                                  }}
                                >
                                  ✅ Delivered {!isStatusChangeAllowed(order.custom_status, 'delivered', order.is_preorder) ? '🚫' : ''}
                                </option>
                                <option 
                                  value="review" 
                                  disabled={!isStatusChangeAllowed(order.custom_status, 'review', order.is_preorder)}
                                  style={{ 
                                    color: !isStatusChangeAllowed(order.custom_status, 'review', order.is_preorder) ? '#ccc' : 'inherit'
                                  }}
                                >
                                  📝 Review Request {!isStatusChangeAllowed(order.custom_status, 'review', order.is_preorder) ? '🚫' : ''}
                                </option>
                                <option
                                  value="dispute"
                                  disabled={!isStatusChangeAllowed(order.custom_status, 'dispute', order.is_preorder)}
                                  style={{
                                    color: !isStatusChangeAllowed(order.custom_status, 'dispute', order.is_preorder) ? '#ccc' : 'inherit'
                                  }}
                                >
                                  🚨 Issue Reported {!isStatusChangeAllowed(order.custom_status, 'dispute', order.is_preorder) ? '🚫' : ''}
                                </option>
                                {/* Show dispute resolution options only when in dispute status */}
                                {order.custom_status === 'dispute' && (
                                  <>
                                    <option 
                                      value="refunded"
                                      style={{ color: '#27ae60', fontWeight: 'bold' }}
                                    >
                                      💰 Refund Processed (End Order)
                                    </option>
                                    <option 
                                      value="replacement_approved"
                                      style={{ color: '#3498db', fontWeight: 'bold' }}
                                    >
                                      📘 Replacement Approved
                            </option>
                                  </>
                                )}
                                {/* Show replacement journey only after replacement is approved */}
                                {isReplacementStatus(order.custom_status) && (
                                  <>
                                    <option
                                      value="replacement_approved"
                                      disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_approved', order.is_preorder)}
                                      style={{
                                        color: !isStatusChangeAllowed(order.custom_status, 'replacement_approved', order.is_preorder) ? '#ccc' : 'inherit'
                                      }}
                                    >
                                      📘 Replacement Approved {!isStatusChangeAllowed(order.custom_status, 'replacement_approved', order.is_preorder) ? '🚫' : ''}
                                    </option>
                                    <option
                                      value="replacement_packing"
                                      disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_packing', order.is_preorder)}
                                      style={{
                                        color: !isStatusChangeAllowed(order.custom_status, 'replacement_packing', order.is_preorder) ? '#ccc' : 'inherit'
                                      }}
                                    >
                                      📦 Replacement Packed {!isStatusChangeAllowed(order.custom_status, 'replacement_packing', order.is_preorder) ? '🚫' : ''}
                                    </option>
                                    <option
                                      value="replacement_shipping_domestic"
                                      disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_shipping_domestic', order.is_preorder)}
                                      style={{
                                        color: !isStatusChangeAllowed(order.custom_status, 'replacement_shipping_domestic', order.is_preorder) ? '#ccc' : 'inherit'
                                      }}
                                    >
                                      🚚 Replacement Shipped Domestic {!isStatusChangeAllowed(order.custom_status, 'replacement_shipping_domestic', order.is_preorder) ? '🚫' : ''}
                                    </option>
                                    {!isIndianOrder(order) && (
                                      <option
                                        value="replacement_shipping_international"
                                        disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_shipping_international', order.is_preorder)}
                                        style={{
                                          color: !isStatusChangeAllowed(order.custom_status, 'replacement_shipping_international', order.is_preorder) ? '#ccc' : 'inherit'
                                        }}
                                      >
                                        ✈️ Replacement Shipped International {!isStatusChangeAllowed(order.custom_status, 'replacement_shipping_international', order.is_preorder) ? '🚫' : ''}
                                      </option>
                                    )}
                                    <option
                                      value="replacement_delivered"
                                      disabled={!isStatusChangeAllowed(order.custom_status, 'replacement_delivered', order.is_preorder)}
                                      style={{
                                        color: !isStatusChangeAllowed(order.custom_status, 'replacement_delivered', order.is_preorder) ? '#ccc' : 'inherit'
                                      }}
                                    >
                                      ✅ Replacement Delivered {!isStatusChangeAllowed(order.custom_status, 'replacement_delivered', order.is_preorder) ? '🚫' : ''}
                                    </option>
                                  </>
                                )}
                                {order.custom_status === 'refunded' && (
                                  <option value="refunded">💰 Refund Processed</option>
                                )}
                          </>
                        )}
                      </select>

                      <div className="action-meta-row">
                        {updatingOrder === order.id && (
                          <div className="updating-spinner">⏳</div>
                        )}

                        {isEmailSent(order.id, order.custom_status) ? (
                          <button
                            type="button"
                            className="email-success-indicator"
                            title={getEmailTooltipText(order)}
                            onClick={() => openEmailHistory(order)}
                          >
                            📧✅
                          </button>
                        ) : isEmailFailed(order.id, order.custom_status) ? (
                          <button
                            type="button"
                            className="email-failed-indicator"
                            title={getEmailTooltipText(order)}
                            onClick={() => openEmailHistory(order)}
                          >
                            📧❌
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="email-neutral-indicator"
                            title={getEmailTooltipText(order)}
                            onClick={() => openEmailHistory(order)}
                          >
                            📧
                          </button>
                        )}
                      </div>
                          
                          {order.custom_status === 'refunded' && (
                            <div className="refunded-status">
                              <span className="refunded-label">💰 Refund Complete</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="order-invoice">
                    <div className="invoice-action-group">
                      <button
                        type="button"
                        className="invoice-url-btn"
                        title={order.invoice_url || 'Open invoice URL'}
                        onClick={() => handleViewInvoice(order)}
                      >
                        URL Invoice
                      </button>
                      <button
                        type="button"
                        className="invoice-download-btn"
                        title="Download Invoice PDF"
                        disabled={downloadingInvoiceId === order.id}
                        onClick={() => handleDownloadInvoice(order)}
                      >
                        {downloadingInvoiceId === order.id ? '...' : 'Download'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {orders.length === 0 && (
          <div className="no-orders">
            <p>No orders found matching your criteria.</p>
          </div>
        )}

        {/* Loading more indicator */}
        {isLoadingMore && (
          <div className="loading-more">
            <div className="loading-spinner-small"></div>
            <p>Loading more orders...</p>
          </div>
        )}

        {/* Show more button (fallback for users who prefer clicking) */}
        {!isLoadingMore && nextOffset !== null && nextOffset !== undefined && nextOffset !== '' && (
          <div className="load-more-container">
            <button onClick={loadMoreOrders} className="load-more-btn">
              Load More Orders ({Math.max(0, totalMatching - orders.length)} remaining)
            </button>
          </div>
        )}
      </div>
        </>
      )}

      <EmailHistoryModal
        order={emailHistoryOrder}
        onClose={() => setEmailHistoryOrder(null)}
        buildOrderEmailDetails={buildOrderEmailDetails}
        resendingEmailKey={resendingEmailKey}
        onResendFailed={resendFailedEmail}
      />

      <DisputeReportModal
        open={showDisputeModal}
        disputeReason={disputeReason}
        disputeComment={disputeComment}
        disputePhotos={disputePhotos}
        processingDispute={processingDispute}
        onReasonChange={setDisputeReason}
        onCommentChange={setDisputeComment}
        onPhotoUpload={handleDisputePhotoUpload}
        onSubmit={submitDispute}
        onClose={closeDisputeModal}
      />

      {/* Product Image Modal */}
      {imageModal.show && (
        <div 
          className="image-modal-overlay"
          onClick={() => setImageModal({ show: false, imageUrl: '', productTitle: '' })}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            cursor: 'pointer'
          }}
        >
          <div 
            className="image-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              position: 'relative',
              cursor: 'default'
            }}
          >
            <button
              onClick={() => setImageModal({ show: false, imageUrl: '', productTitle: '' })}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                fontSize: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001
              }}
            >
              ×
            </button>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#1a202c' }}>
              {imageModal.productTitle}
            </h3>
            <img 
              src={imageModal.imageUrl} 
              alt={imageModal.productTitle}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f1f5f9" width="400" height="300"/%3E%3Ctext fill="%2394a3b8" font-family="sans-serif" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage not available%3C/text%3E%3C/svg%3E';
              }}
            />
          </div>
        </div>
      )}

      <DashboardOverlays
        notification={notification}
        onHideNotification={hideNotification}
        popupState={popupState}
        onClosePopup={closePopup}
        onPopupInputChange={(value) => setPopupState((prev) => ({ ...prev, inputValue: value }))}
      />
        </>
      )}
    </div>
  );
};

export default OrderDashboard; 
